/**
 * SessionsListPage - List of all sessions with tabs (Waiting/Live, Completed)
 * Displays sessions in a table with filtering and action buttons
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SessionWithPlayers, SessionStatus } from '@/types/session';
import { ClassWithMetrics } from '@/types/class';
import { sessionsService } from '@/services/sessions';
import { classesService } from '@/services/classes';

type TabKey = 'active' | 'completed';

export function SessionsListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<TabKey>('active');
  const [sessions, setSessions] = useState<SessionWithPlayers[]>([]);
  const [classes, setClasses] = useState<ClassWithMetrics[]>([]);
  const [classFilter, setClassFilter] = useState<string>(searchParams.get('classId') || 'all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [classFilter]);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);

      const classIdParam = classFilter === 'all' ? undefined : classFilter;
      const [sessionsResponse, classesResponse] = await Promise.all([
        sessionsService.listSessions(classIdParam),
        classesService.listClasses(),
      ]);

      setSessions(sessionsResponse.sessions);
      setClasses(classesResponse);
    } catch (err: any) {
      console.error('Failed to load sessions:', err);
      setError(err.response?.data?.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }

  function getClassName(classId?: string): string {
    if (!classId) return 'Uncategorized';
    const classItem = classes.find((c) => c.classId === classId);
    return classItem?.className || 'Unknown';
  }

  function filterSessions(tab: TabKey): SessionWithPlayers[] {
    if (tab === 'active') {
      return sessions.filter(
        (s) => s.status === SessionStatus.WAITING || s.status === SessionStatus.LIVE
      );
    } else {
      return sessions.filter((s) => s.status === SessionStatus.COMPLETE);
    }
  }

  function formatDateTime(isoString: string | undefined): string {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function calculateDuration(startedAt?: string, completedAt?: string): string {
    if (!startedAt || !completedAt) return 'N/A';

    const start = new Date(startedAt).getTime();
    const end = new Date(completedAt).getTime();
    const durationMs = end - start;

    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }

  async function handleStartSession(sessionId: string) {
    if (!confirm('Start this session? All players will be assigned roles.')) {
      return;
    }

    try {
      setActionLoading(sessionId);
      await sessionsService.startSession(sessionId);
      await loadData(); // Refresh list
      alert('Session started successfully!');
    } catch (err: any) {
      console.error('Failed to start session:', err);
      alert(err.response?.data?.message || 'Failed to start session');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleCompleteSession(sessionId: string) {
    if (!confirm('Complete this session? This will end the game and archive results.')) {
      return;
    }

    try {
      setActionLoading(sessionId);
      await sessionsService.completeSession(sessionId);
      await loadData(); // Refresh list
      alert('Session completed successfully!');
    } catch (err: any) {
      console.error('Failed to complete session:', err);
      alert(err.response?.data?.message || 'Failed to complete session');
    } finally {
      setActionLoading(null);
    }
  }

  function canStartSession(session: SessionWithPlayers): boolean {
    return (
      session.status === SessionStatus.WAITING &&
      session.joinedCount >= session.requiredPlayers
    );
  }

  const activeSessions = filterSessions('active');
  const completedSessions = filterSessions('completed');

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#12356B] mb-4" />
          <p className="text-slate-600">Loading sessions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-800 mb-4">
            <strong>Error:</strong> {error}
          </div>
          <button onClick={loadData} className="px-6 py-2 bg-[#367A25] text-white rounded-lg hover:bg-[#2d6820] transition-colors">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent">
      <div className="w-[min(80vw,100%)] mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-end gap-4 mb-6">
          <h1 className="text-3xl font-bold text-[#12356B]">All Sessions</h1>

          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div>
              <label htmlFor="class-filter" className="block text-sm font-medium text-slate-700 mb-2">
                Filter by Class
              </label>
              <select
                id="class-filter"
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
                className="w-full sm:w-64 px-3 py-2 border border-[#CED4DE] rounded-md focus:outline-none focus:ring-2 focus:ring-[#12356B]/25 focus:border-[#12356B]"
              >
                <option value="all">All Classes</option>
                <option value="uncategorized">Uncategorized</option>
                {classes.map((classItem) => (
                  <option key={classItem.classId} value={classItem.classId}>
                    {classItem.className} ({classItem.sessionCount})
                  </option>
                ))}
              </select>
            </div>

            <button onClick={() => navigate('/login/teacher/new')} className="px-6 py-2 bg-[#367A25] text-white rounded-lg hover:bg-[#2d6820] transition-colors shadow-sm whitespace-nowrap">
              Create Session
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[#CED4DE] mb-6">
          <button
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'active'
                ? 'text-[#12356B] border-b-2 border-[#12356B]'
                : 'text-slate-600 hover:text-[#12356B]'
            }`}
            onClick={() => setActiveTab('active')}
          >
            Waiting & Live ({activeSessions.length})
          </button>
          <button
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'completed'
                ? 'text-[#12356B] border-b-2 border-[#12356B]'
                : 'text-slate-600 hover:text-[#12356B]'
            }`}
            onClick={() => setActiveTab('completed')}
          >
            Completed ({completedSessions.length})
          </button>
        </div>

      {/* Active Sessions Tab */}
      {activeTab === 'active' && (
        <div>
          {activeSessions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600 text-lg mb-2">No active sessions</p>
              <p className="text-slate-500">
                Create a new session to get started.
              </p>
            </div>
          ) : (
            <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full min-w-[980px]">
                <thead className="bg-[#E2E5EC]/60 border-b border-[#CED4DE]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider"># Joined</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider w-[220px] min-w-[220px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E5EC]">
                  {activeSessions.map((session) => (
                    <tr key={session.sessionId} className="hover:bg-[#E2E5EC]/35">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 bg-[#E2E5EC] text-[#12356B] rounded text-sm font-semibold tracking-[0.08em]">{session.code}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-900">
                        <div className="font-medium">{session.sessionName || 'Unnamed Session'}</div>
                        <div className="mt-1">
                          <span className="px-2 py-1 bg-[#E2E5EC] text-[#12356B] rounded text-xs">
                            {getClassName(session.classId)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          session.status === SessionStatus.WAITING
                            ? 'bg-[#DD6832]/10 text-[#DD6832]'
                            : session.status === SessionStatus.LIVE
                            ? 'bg-[#367A25]/10 text-[#367A25]'
                            : 'bg-[#E2E5EC] text-[#12356B]'
                        }`}>
                          {session.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                        {session.joinedCount} / {session.requiredPlayers}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{formatDateTime(session.createdAt)}</td>
                      <td className="px-6 py-4 w-[220px] min-w-[220px]">
                        <div className="flex flex-col gap-1">
                          <div className="flex gap-2 flex-wrap">
                            <button
                              onClick={() =>
                                navigate(`/login/teacher/sessions/${session.sessionId}`)
                              }
                              className="px-3 py-1 bg-[#12356B] text-white rounded hover:bg-[#0d2850] transition-colors text-sm disabled:opacity-50 min-w-[84px] font-medium"
                              disabled={actionLoading === session.sessionId}
                            >
                              View
                            </button>

                            {session.status === SessionStatus.WAITING && (
                              <button
                                onClick={() => handleStartSession(session.sessionId)}
                                className="px-3 py-1 bg-[#367A25]/10 text-[#367A25] rounded hover:bg-[#367A25]/20 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed min-w-[84px]"
                                disabled={
                                  !canStartSession(session) ||
                                  actionLoading === session.sessionId
                                }
                                title={
                                  !canStartSession(session)
                                    ? 'Not enough players to start'
                                    : 'Start session and assign roles'
                                }
                              >
                                {actionLoading === session.sessionId
                                  ? 'Starting...'
                                  : 'Start'}
                              </button>
                            )}

                            {session.status === SessionStatus.LIVE && (
                              <button
                                onClick={() => handleCompleteSession(session.sessionId)}
                                className="px-3 py-1 bg-[#DD6832]/10 text-[#DD6832] rounded hover:bg-[#DD6832]/20 transition-colors text-sm disabled:opacity-50 min-w-[84px]"
                                disabled={actionLoading === session.sessionId}
                              >
                                {actionLoading === session.sessionId
                                  ? 'Ending...'
                                  : 'End'}
                              </button>
                            )}
                          </div>
                          {session.status === SessionStatus.WAITING && !canStartSession(session) && (
                            <span className="text-xs text-[#DD6832]">
                              Waiting for {session.requiredPlayers - session.joinedCount} more player{session.requiredPlayers - session.joinedCount !== 1 ? 's' : ''} to join
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Completed Sessions Tab */}
      {activeTab === 'completed' && (
        <div>
          {completedSessions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600 text-lg mb-2">No completed sessions</p>
              <p className="text-slate-500">
                Completed sessions will appear here after you end them.
              </p>
            </div>
          ) : (
            <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full min-w-[1080px]">
                <thead className="bg-[#E2E5EC]/60 border-b border-[#CED4DE]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider"># Players</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Started</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Completed</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Duration</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider w-[150px] min-w-[150px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E5EC]">
                  {completedSessions.map((session) => (
                    <tr key={session.sessionId} className="hover:bg-[#E2E5EC]/35">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 bg-[#E2E5EC] text-[#12356B] rounded text-sm font-semibold tracking-[0.08em]">{session.code}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-900">
                        <div className="font-medium">{session.sessionName || 'Unnamed Session'}</div>
                        <div className="mt-1">
                          <span className="px-2 py-1 bg-[#E2E5EC] text-[#12356B] rounded text-xs">
                            {getClassName(session.classId)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{session.joinedCount}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{formatDateTime(session.startedAt)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{formatDateTime(session.completedAt)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                        {calculateDuration(session.startedAt, session.completedAt)}
                      </td>
                      <td className="px-6 py-4 w-[150px] min-w-[150px]">
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() =>
                              navigate(`/login/teacher/sessions/${session.sessionId}`)
                            }
                            className="px-3 py-1 bg-[#12356B] text-white rounded hover:bg-[#0d2850] transition-colors text-sm min-w-[84px] font-medium"
                          >
                            View
                          </button>
                          <button
                            onClick={() =>
                              navigate(`/login/teacher/sessions/${session.sessionId}/results`)
                            }
                            className="px-3 py-1 bg-[#DD6832] text-white rounded hover:bg-[#c4562a] transition-colors text-sm min-w-[84px] font-medium"
                          >
                            Results
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
